import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Sparkles, Search, Loader2, ToggleLeft, ToggleRight, TrendingUp, DollarSign, Building2 } from "lucide-react";

/**
 * Super-admin panel to enable/disable "AI ocena starosti" per LD (paid service).
 * Also shows usage stats (last 30 days, estimated cost).
 */
export const SuperAdminAIJaw = () => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [lds, setLds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [busyLd, setBusyLd] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ldsRes, statsRes] = await Promise.all([
        axios.get(`${API}/superadmin/lovske-druzine`, { headers: authHeaders }),
        axios.get(`${API}/superadmin/ai-jaw/stats`, { headers: authHeaders }),
      ]);
      setLds((ldsRes.data || []).filter((l) => l.aktivna));
      setStats(statsRes.data);
    } catch (e) {
      toast.error("Napaka pri branju podatkov");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const toggleLd = async (ld, next) => {
    setBusyLd(ld.id);
    try {
      await axios.put(
        `${API}/superadmin/lovske-druzine/${ld.id}/ai-jaw`,
        { enabled: next },
        { headers: authHeaders }
      );
      setLds((prev) => prev.map((x) => (x.id === ld.id ? { ...x, ai_jaw_enabled: next } : x)));
      toast.success(next ? `AI ocena vklopljena za ${ld.naziv}` : `AI ocena izklopljena za ${ld.naziv}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    } finally {
      setBusyLd(null);
    }
  };

  const filtered = lds.filter((l) =>
    !search || (l.naziv || "").toLowerCase().includes(search.toLowerCase()) || (l.koda || "").toLowerCase().includes(search.toLowerCase())
  );

  const usageByLdId = {};
  (stats?.last_30d_per_ld || []).forEach((e) => { usageByLdId[e._id] = e; });

  return (
    <div className="space-y-4" data-testid="sa-aijaw-panel">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-600" />
            AI ocena starosti divjadi — dostop po LD-jih
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Vklopi ali izklopi plačljivo storitev AI ocenjevanja starosti parkljarjev (Gemini 3.1 Pro + priložen referenčni PDF ZGS).
            Priporočena cena: <strong>10–12 € / leto / LD</strong>. Ocenjen strošek: ~<strong>0,05 € / analiza</strong>.
          </p>
        </CardHeader>
      </Card>

      {/* Global stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">LD z vklopom</p>
            <p className="text-2xl font-bold text-primary" data-testid="sa-aijaw-lds-enabled">{stats.lds_enabled}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Analize skupaj</p>
            <p className="text-2xl font-bold">{stats.total_analyses}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase">Delež fallback</p>
            <p className="text-2xl font-bold text-amber-600">{Math.round(stats.fallback_ratio * 100)}%</p>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><DollarSign className="h-3 w-3" />Strošek 30 dni</p>
            <p className="text-2xl font-bold text-emerald-700" data-testid="sa-aijaw-cost">€{stats.estimated_cost_last_30d_eur?.toFixed(2)}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Search + list */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Išči LD…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="sa-aijaw-search"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <ul className="divide-y divide-slate-200 max-h-[500px] overflow-y-auto">
              {filtered.map((ld) => {
                const use = usageByLdId[ld.id];
                const enabled = !!ld.ai_jaw_enabled;
                return (
                  <li key={ld.id} className="flex items-center gap-3 py-2.5" data-testid={`sa-aijaw-ld-${ld.koda}`}>
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{ld.naziv}</p>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{ld.stevilo_clanov || 0} članov</span>
                        {use && (
                          <span className="inline-flex items-center gap-0.5">
                            · <TrendingUp className="h-3 w-3" /> {use.count} analiz / 30 dni
                            {use.pro_calls > 0 && <span className="text-amber-600 ml-1">({use.pro_calls} Pro)</span>}
                          </span>
                        )}
                      </div>
                    </div>
                    {enabled ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Vklopljena</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">Izklopljena</Badge>
                    )}
                    <Button
                      size="sm"
                      variant={enabled ? "outline" : "default"}
                      onClick={() => toggleLd(ld, !enabled)}
                      disabled={busyLd === ld.id}
                      data-testid={`sa-aijaw-toggle-${ld.koda}`}
                    >
                      {busyLd === ld.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        enabled ? <><ToggleRight className="h-4 w-4 mr-1" />Izklopi</> : <><ToggleLeft className="h-4 w-4 mr-1" />Vklopi</>
                      )}
                    </Button>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Ni zadetkov</p>
              )}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
